// src/components/pages/AdminPage/AdminPage.tsx
import React, { useState, useEffect } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { useTelegramContext } from '../../services/telegramContext';
import { useNavigate } from 'react-router-dom';
import { adminService, AdminStats, ReferralStats, PaymentRecord, MonthlyStats } from '../../services/adminService';
import './AdminPage.scss';

type SortField = 'total_referrals' | 'paying_referrals' | 'conversion_rate' | 'total_amount';
type SortOrder = 'asc' | 'desc';

const ADMIN_IDS = (process.env.REACT_APP_ADMIN_IDS || process.env.NEXT_PUBLIC_ADMIN_IDS || '').split(',').map(id => id.trim()).filter(id => id);

const AdminPage: React.FC = () => {
    const navigate = useNavigate();
    const telegramContext = useTelegramContext();

    const [stats, setStats] = useState<AdminStats | null>(null);
    const [referralStats, setReferralStats] = useState<ReferralStats[]>([]);
    const [payments, setPayments] = useState<PaymentRecord[]>([]);
    const [paymentsTotal, setPaymentsTotal] = useState(0);
    const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'stats' | 'referrals' | 'payments'>('stats');
    const [expandedReferrer, setExpandedReferrer] = useState<string | null>(null);

    const [sortField, setSortField] = useState<SortField>('total_referrals');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

    const [paymentsOffset, setPaymentsOffset] = useState(0);
    const [paymentsLimit] = useState(50);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const { loadUserData } = useAppStore();

    useEffect(() => {
        if (telegramContext.isReady) {
            const isTelegram = telegramContext.isTelegram;
            const userId = telegramContext.user?.id?.toString() || null;
            const initData = telegramContext.initData;

            adminService.setTelegramContext(isTelegram, userId, initData);

            checkAdminAndLoadData();
        }
    }, [telegramContext.isReady, telegramContext.isTelegram, telegramContext.user, telegramContext.initData]);

    const checkAdminAndLoadData = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessCheck = await adminService.checkAdminAccess();

            if (!accessCheck.isAdmin) {
                navigate('/', { replace: true });
                return;
            }
            if (accessCheck.telegramId) {
                await loadUserData(accessCheck.telegramId);
            }

            await Promise.all([
                loadStats(),
                loadReferralStats(),
                loadPayments()
            ]);

        } catch (err: any) {
            setError('Ошибка загрузки данных: ' + (err.message || 'Неизвестная ошибка'));
        } finally {
            setIsLoading(false);
        }
    };

    const loadStats = async () => {
        const statsData = await adminService.getStats();
        if (statsData) {
            setStats(statsData);
        }
    };

    const loadReferralStats = async () => {
        const referralData = await adminService.getReferralStats();
        setReferralStats(referralData);
    };

    const loadPayments = async () => {
        const { payments: paymentsData, total, monthlyStats: monthlyStatsData } = await adminService.getAllPayments(paymentsLimit, paymentsOffset);
        setPayments(paymentsData);
        setPaymentsTotal(total);
        setMonthlyStats(monthlyStatsData);
    };

    const loadMorePayments = async () => {
        if (isLoadingMore) return;
        setIsLoadingMore(true);
        const newOffset = paymentsOffset + paymentsLimit;
        const { payments: newPayments } = await adminService.getAllPayments(paymentsLimit, newOffset);
        setPayments([...payments, ...newPayments]);
        setPaymentsOffset(newOffset);
        setIsLoadingMore(false);
    };

    const getSortedReferrals = () => {
        return [...referralStats].sort((a, b) => {
            let aVal: number, bVal: number;
            switch (sortField) {
                case 'total_referrals':
                    aVal = a.total_referrals;
                    bVal = b.total_referrals;
                    break;
                case 'paying_referrals':
                    aVal = a.paying_referrals;
                    bVal = b.paying_referrals;
                    break;
                case 'conversion_rate':
                    aVal = a.conversion_rate;
                    bVal = b.conversion_rate;
                    break;
                case 'total_amount':
                    aVal = a.total_amount;
                    bVal = b.total_amount;
                    break;
                default:
                    aVal = a.total_referrals;
                    bVal = b.total_referrals;
            }
            return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
        });
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'RUB',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatShortDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const parseLabelSub = (label: string) => {
        const parts = label.split('_');
        if (parts[0] === 'extend') {
            return {
                type: 'Продление',
                devices: parseInt(parts[1].replace('dev', '')),
                months: parseInt(parts[2].replace('m', ''))
            };
        }
        return {
            type: 'Подписка',
            months: parseInt(parts[1].replace('m', '')),
            devices: parseInt(parts[2].replace('dev', ''))
        };
    };

    const sortedReferrals = getSortedReferrals();

    if (isLoading) {
        return (
            <div className="admin-page">
                <div className="admin-loading">
                    <div className="spinner"></div>
                    <span>Загрузка данных...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page">
                <div className="admin-error">
                    <div className="error-icon">⚠️</div>
                    <h2>Ошибка</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <button className="back-button" onClick={() => window.history.back()}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1>Админ панель</h1>
                <div className="admin-tabs">
                    <button
                        className={`admin-tab ${activeTab === 'stats' ? 'active' : ''}`}
                        onClick={() => setActiveTab('stats')}
                    >
                        📊 Статистика
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'referrals' ? 'active' : ''}`}
                        onClick={() => setActiveTab('referrals')}
                    >
                        🔗 Рефералы
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'payments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('payments')}
                    >
                        💳 Платежи
                    </button>
                </div>
            </div>

            {activeTab === 'stats' && stats && (
                <div className="admin-stats">
                    <div className="stats-cards">
                        <div className="stat-card">
                            <div className="stat-icon">💰</div>
                            <div className="stat-content">
                                <div className="stat-value">{formatCurrency(stats.total_payments.amount)}</div>
                                <div className="stat-label">Общая выручка</div>
                                <div className="stat-sub">{stats.total_payments.count} платежей</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">👥</div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.active_subscriptions}</div>
                                <div className="stat-label">Активных подписок</div>
                                <div className="stat-sub">из {stats.total_users} пользователей</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">📉</div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.churned_last_30d}</div>
                                <div className="stat-label">Отток за 30 дней</div>
                                <div className="stat-sub">с истекшей подпиской</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon">📈</div>
                            <div className="stat-content">
                                <div className="stat-value">
                                    {stats.total_users > 0
                                        ? Math.round((stats.active_subscriptions / stats.total_users) * 100)
                                        : 0}%
                                </div>
                                <div className="stat-label">Конверсия в подписку</div>
                                <div className="stat-sub">активные / всего</div>
                            </div>
                        </div>
                    </div>

                    {monthlyStats.length > 0 && (
                        <div className="stats-chart">
                            <h3>📅 Помесячная статистика</h3>
                            <div className="monthly-stats-grid">
                                {monthlyStats.map((stat) => (
                                    <div key={stat.month} className="monthly-card">
                                        <div className="monthly-month">{stat.month}</div>
                                        <div className="monthly-details">
                                            <div className="monthly-item">
                                                <span className="monthly-label">Платежей:</span>
                                                <span className="monthly-value">{stat.count}</span>
                                            </div>
                                            <div className="monthly-item">
                                                <span className="monthly-label">Сумма:</span>
                                                <span className="monthly-value highlight">{formatCurrency(stat.total)}</span>
                                            </div>
                                            <div className="monthly-item">
                                                <span className="monthly-label">Пользователей:</span>
                                                <span className="monthly-value">{stat.unique_users}</span>
                                            </div>
                                            <div className="monthly-item">
                                                <span className="monthly-label">Средний чек:</span>
                                                <span className="monthly-value">{formatCurrency(stat.total / stat.count)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="stats-chart">
                        <h3>📈 Динамика платежей по месяцам</h3>
                        <div className="chart-container">
                            <div className="chart-bars">
                                {stats.monthly_payments.map((item, index) => {
                                    const maxAmount = Math.max(...stats.monthly_payments.map(p => p.amount), 1);
                                    const height = (item.amount / maxAmount) * 150;
                                    return (
                                        <div key={index} className="chart-bar-wrapper">
                                            <div className="chart-bar" style={{ height: `${height}px` }}>
                                                <span className="chart-value">{formatCurrency(item.amount)}</span>
                                            </div>
                                            <div className="chart-label">{item.month?.slice(5)}</div>
                                            <div className="chart-count">{item.count} шт</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="chart-note">
                            * Данные из таблицы payment (успешные платежи)
                        </div>
                    </div>

                    <div className="stats-chart">
                        <h3>👤 Новые пользователи по месяцам</h3>
                        <div className="chart-container">
                            <div className="chart-bars">
                                {stats.new_users_growth?.map((item, index) => {
                                    const maxUsers = Math.max(...stats.new_users_growth.map(g => g.new_users), 1);
                                    const height = (item.new_users / maxUsers) * 150;
                                    return (
                                        <div key={index} className="chart-bar-wrapper">
                                            <div className="chart-bar users-bar" style={{ height: `${height}px` }}>
                                                <span className="chart-value">{item.new_users}</span>
                                            </div>
                                            <div className="chart-label">{item.month?.slice(5)}</div>
                                            <div className="chart-count">{item.new_users} новых</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="chart-note">
                            * Данные из таблицы user (дата регистрации)
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'referrals' && (
                <div className="admin-referrals">
                    <div className="referrals-stats-summary">
                        <div className="summary-card">
                            <div className="summary-value">{referralStats.length}</div>
                            <div className="summary-label">Рефереров</div>
                        </div>
                        <div className="summary-card">
                            <div className="summary-value">
                                {referralStats.reduce((sum, r) => sum + r.total_referrals, 0)}
                            </div>
                            <div className="summary-label">Всего рефералов</div>
                        </div>
                        <div className="summary-card">
                            <div className="summary-value">
                                {referralStats.reduce((sum, r) => sum + r.paying_referrals, 0)}
                            </div>
                            <div className="summary-label">Оплативших</div>
                        </div>
                        <div className="summary-card">
                            <div className="summary-value">
                                {Math.round(
                                    (referralStats.reduce((sum, r) => sum + r.paying_referrals, 0) /
                                        referralStats.reduce((sum, r) => sum + r.total_referrals, 0)) * 100
                                ) || 0}%
                            </div>
                            <div className="summary-label">Конверсия</div>
                        </div>
                    </div>

                    <div className="referrals-list">
                        {sortedReferrals.map((referrer) => (
                            <div key={referrer.referrer_id} className="referrer-card">
                                <div
                                    className="referrer-card-header"
                                    onClick={() => setExpandedReferrer(
                                        expandedReferrer === referrer.referrer_id ? null : referrer.referrer_id
                                    )}
                                >
                                    <div className="referrer-info">
                                        <div className="referrer-avatar">
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <circle cx="12" cy="8" r="4" />
                                                <path d="M5 18v2c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2v-2" />
                                            </svg>
                                        </div>
                                        <div className="referrer-details">
                                            <div className="referrer-name">{referrer.username || referrer.referrer_id.slice(0, 10)}</div>
                                            <div className="referrer-id">{referrer.referrer_id}</div>
                                        </div>
                                    </div>
                                    <div className="referrer-stats">
                                        <div className="stat-badge">
                                            <span className="stat-number">{referrer.total_referrals}</span>
                                            <span className="stat-label-small">реф</span>
                                        </div>
                                        <div className="stat-badge">
                                            <span className="stat-number">{referrer.paying_referrals}</span>
                                            <span className="stat-label-small">опл</span>
                                        </div>
                                        <div className={`conversion-badge ${referrer.conversion_rate >= 30 ? 'high' : referrer.conversion_rate >= 10 ? 'medium' : 'low'}`}>
                                            {referrer.conversion_rate}%
                                        </div>
                                        <button className="expand-btn">
                                            {expandedReferrer === referrer.referrer_id ? '▲' : '▼'}
                                        </button>
                                    </div>
                                </div>

                                {expandedReferrer === referrer.referrer_id && (
                                    <div className="referrer-card-details">
                                        <div className="referrer-summary">
                                            <div className="summary-row">
                                                <span>💰 Сумма покупок:</span>
                                                <span className="highlight">{formatCurrency(referrer.total_amount)}</span>
                                            </div>
                                            <div className="summary-row">
                                                <span>🎁 Заработано дней:</span>
                                                <span className="highlight">{referrer.total_earned} дней</span>
                                            </div>
                                        </div>

                                        <div className="referrals-list-detail">
                                            <h4>Приглашенные пользователи</h4>
                                            {referrer.referrals.map((ref) => (
                                                <div key={ref.user_id} className="referral-item">
                                                    <div className="referral-user">
                                                        <div className="referral-name">{ref.username || ref.user_id.slice(0, 12)}</div>
                                                        <div className="referral-date">{ref.created_at ? formatShortDate(ref.created_at) : '—'}</div>
                                                    </div>
                                                    <div className="referral-stats-detail">
                                                        <span className="referral-amount">{formatCurrency(ref.purchase_amount)}</span>
                                                        <span className={`status-badge ${ref.has_payment ? 'paid' : 'pending'}`}>
                                                            {ref.has_payment ? '✅ Оплатил' : '⏳ Не оплатил'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {referralStats.length === 0 && (
                        <div className="admin-empty">
                            <div className="empty-icon">📭</div>
                            <p>Нет данных о рефералах</p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'payments' && (
                <div className="admin-payments">
                    <div className="payments-stats-summary">
                        <div className="summary-card">
                            <div className="summary-value">{paymentsTotal}</div>
                            <div className="summary-label">Всего платежей</div>
                        </div>
                        <div className="summary-card">
                            <div className="summary-value">
                                {formatCurrency(payments.reduce((sum, p) => sum + p.amount, 0))}
                            </div>
                            <div className="summary-label">Общая сумма</div>
                        </div>
                        <div className="summary-card">
                            <div className="summary-value">
                                {payments.length > 0 ? formatCurrency(payments.reduce((sum, p) => sum + p.amount, 0) / payments.length) : '0 ₽'}
                            </div>
                            <div className="summary-label">Средний чек</div>
                        </div>
                    </div>

                    <div className="payments-list">
                        {payments.map((payment) => {
                            const labelInfo = parseLabelSub(payment.label_sub);
                            return (
                                <div key={payment.id} className="payment-card">
                                    <div className="payment-card-header">
                                        <div className="payment-user">
                                            <div className="payment-user-id">{payment.user_id}</div>
                                            {payment.username && <div className="payment-username">@{payment.username}</div>}
                                        </div>
                                        <div className="payment-amount">{formatCurrency(payment.amount)}</div>
                                    </div>
                                    <div className="payment-card-details">
                                        <div className="payment-detail">
                                            <span className="detail-label">Тип:</span>
                                            <span className={`type-badge ${labelInfo.type === 'Подписка' ? 'subscription' : 'extend'}`}>
                                                {labelInfo.type}
                                            </span>
                                        </div>
                                        <div className="payment-detail">
                                            <span className="detail-label">Период:</span>
                                            <span>{labelInfo.months} мес</span>
                                        </div>
                                        <div className="payment-detail">
                                            <span className="detail-label">Устройств:</span>
                                            <span>{labelInfo.devices}</span>
                                        </div>
                                        <div className="payment-detail">
                                            <span className="detail-label">Дата:</span>
                                            <span>{formatDate(payment.payment_time)}</span>
                                        </div>
                                        {payment.email && (
                                            <div className="payment-detail">
                                                <span className="detail-label">Email:</span>
                                                <span className="payment-email">{payment.email}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {payments.length < paymentsTotal && (
                        <button className="load-more-btn" onClick={loadMorePayments} disabled={isLoadingMore}>
                            {isLoadingMore ? 'Загрузка...' : 'Загрузить еще'}
                        </button>
                    )}

                    {payments.length === 0 && (
                        <div className="admin-empty">
                            <div className="empty-icon">📭</div>
                            <p>Нет успешных платежей</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminPage;